import React, {useEffect, useState} from 'react';
import {
  FEATURE_UPGRADE_CATALOG,
  GENERIC_UPGRADE_CONTENT,
} from '../Constants/FeatureUpgradeCatalog';
import {useTheme} from '../HelperClasses/ThemeContext';
import {useLocale} from '../Localization/LocaleContext';
import {trackPlanClick} from '../Services/PlanTracking';
import {planKitLines, planLimitLines, planUpgradeHint} from '../Services/PlanLimits';
import {selectPlan, loadPlanPrices, getPlanFamily, getPlanRank, isPlanHidden, PLAN_FAMILIES} from '../Services/PlanActions';
import AppConstants from '../Constants/AppConstants';
import {MARKETPLACE_PLANS, INDIE_PLANS} from '../Constants/PlanPricingData';
import {getSelectedTenantDetails} from '../Constants/Functions';
import './FeatureUpgradePanel.css';
import {localizeUpgradeContent} from './upgradePageLocalization';

const CREATOR_PLAN_CONTENT = {
  go: {
    name: 'Go',
    label: 'Best next step',
    description: 'Everything a growing store needs: remove branding, add your domain, and unlock more tools.',
    benefits: [],
  },
  genius: {
    name: 'Genius',
    label: 'Everything unlocked',
    description: 'Full AI stack, advanced marketing, and everything unlocked for stores serious about growth.',
    benefits: [],
  },
};
// Entry plan, offered only when the workspace must choose a plan (it unlocks no gated feature).
const BASIC_PLAN_CONTENT = {name: 'Basic', benefits: []};
// Upgrade plan name -> pricing page plan id; cards use the pricing page copy.
const PRICING_PLAN_IDS = {
  basic: '2025-superstarter',
  starter: '2025-starter',
  growth: '2025-growth',
  scale: '2025-scale',
  go: 'go',
  genius: 'genius',
};
// Shown when the workspace must choose a paid plan before continuing.
const BLOCKING_CONTENT = {
  eyebrow: 'A note from the Tradly team',
  title: 'Help us keep Tradly going',
  description:
    "Our costs have gone up, and to keep building and supporting Tradly we need our community's support. To continue with your workspace, please choose a plan. Your store and your data are safe and waiting for you. Thank you for being part of Tradly.",
  more:
    "Since 2019 we have offered Tradly with a free package, with a lot included at no cost, because we wanted anyone to be able to start something. We were glad to do it, but it is no longer something we can sustain on our own. We plan to bring a free plan back once the business crosses a certain stage, so we can give back to the community that got us here.",
  intro: 'Choose the plan that fits you today. You can change it any time.',
};

const FeatureUpgradePanel = ({featureKey, isOpen, onClose, contextLabel, blocking = false}) => {
  const {colors, isDark} = useTheme();
  const {translate: t, formatCurrency} = useLocale();
  const catalogContent = FEATURE_UPGRADE_CATALOG[featureKey] || GENERIC_UPGRADE_CONTENT;
  const content = localizeUpgradeContent(blocking ? {...catalogContent, ...BLOCKING_CONTENT} : catalogContent, t);
  const [busyPlan, setBusyPlan] = useState('');
  const [planError, setPlanError] = useState('');
  const [prices, setPrices] = useState({});
  const [showMore, setShowMore] = useState(false);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    loadPlanPrices('upgrade_panel').then(setPrices).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = event => {
      if (event.key === 'Escape' && !blocking) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, blocking]);

  if (!isOpen) return null;

  const panelBackground = isDark ? '#202321' : '#ffffff';
  const mutedSurface = isDark ? '#182522' : '#eff8f6';
  const border = isDark ? '#3a4743' : '#d9e5e2';
  const workflowText = isDark ? '#c5d0ce' : '#52615e';
  // Offer only the next steps inside the tenant's own pricing family. Marketplace and
  // Creator are separate ladders; à la carte tenants buy credits instead of a plan.
  const lookupKey = AppConstants.subscriptionLookupKey;
  // A paid plan decides the family. On Community, the tenant's "Product Interested"
  // choice (sub_type: 1 Marketplace, 2 Single Store) does.
  const tenant = getSelectedTenantDetails(AppConstants.tenant_list || [])?.[0];
  const interestedFamily = Number(tenant?.sub_type) === 2 ? 'creator' : 'marketplace';
  const family = getPlanFamily(lookupKey) || interestedFamily;
  const rank = getPlanRank(lookupKey);
  const isAlaCarte =
    (tenant?.metadata || AppConstants.tenant_metadata)?.product_model === 'ala_carte';
  const catalogPlans = content.plans || GENERIC_UPGRADE_CONTENT.plans;
  const familyPlans =
    family === 'creator'
      ? PLAN_FAMILIES.creator.map(key => localizeUpgradeContent(CREATOR_PLAN_CONTENT[key], t))
      : [...(blocking ? [localizeUpgradeContent(BASIC_PLAN_CONTENT, t)] : []), ...catalogPlans];
  const plans = familyPlans.filter(
    plan =>
      !isPlanHidden(plan.name) &&
      PLAN_FAMILIES[family].indexOf(String(plan.name).toLowerCase()) >=
      Math.max(rank + 1, content.minimumRank || 0),
  );
  if (!isAlaCarte && !plans.length) return null;

  // Same title, description and features as the pricing page (and the workspace plans page)
  const withPricingCopy = plan => {
    const copyFamily = family === 'creator' ? 'indie' : 'marketplace';
    const id = PRICING_PLAN_IDS[String(plan.name).toLowerCase()];
    const item = (family === 'creator' ? INDIE_PLANS : MARKETPLACE_PLANS).find(candidate => candidate.id === id);
    if (!item) return plan;
    const copyKey = `workspace.plans.${copyFamily}.${item.id}`;
    return {
      ...plan,
      title: t(`${copyKey}.title`, item.title),
      label: '',
      description: t(`${copyKey}.description`, item.description.trim()),
      benefits: (item.features || []).map((feature, index) => t(`${copyKey}.feature_${index + 1}.title`, feature.title)),
    };
  };
  const planCards = plans.map(withPricingCopy);
  const priceFor = plan => {
    const name = String(plan.name).toLowerCase();
    return annual ? prices[`${name}:yearly`] : prices[name];
  };
  const hasYearly = planCards.some(plan => prices[`${String(plan.name).toLowerCase()}:yearly`]);
  // Yearly saving against twelve monthly payments, for the price card and the toggle badge
  const yearlySavingFor = plan => {
    const name = String(plan.name).toLowerCase();
    const monthly = prices[name];
    const yearly = prices[`${name}:yearly`];
    if (!monthly || !yearly) return null;
    const saving = Number(monthly.amount) * 12 - Number(yearly.amount);
    return saving > 0 ? {amount: saving, percent: Math.round((saving / (Number(monthly.amount) * 12)) * 100)} : null;
  };
  const yearlyDiscountPercent = Math.max(0, ...planCards.map(plan => yearlySavingFor(plan)?.percent || 0));

  // Activates the chosen plan: Stripe checkout from Community or a trial, a direct change from a paid plan.
  const choosePlan = async planName => {
    trackPlanClick('upgrade_panel', planName);
    setBusyPlan(planName);
    setPlanError('');
    const price = priceFor({name: planName});
    const result = await selectPlan({planName: price?.key || planName, annual});
    if (result.ok && !result.redirected) {
      onClose();
      return;
    }
    if (!result.ok) setPlanError(result.error);
    setBusyPlan('');
  };

  // Plan details are maintained on the website only.
  const pricingUrl = isAlaCarte
    ? 'https://tradly.app/pricing/ala-carte'
    : family === 'creator'
      ? 'https://tradly.app/pricing/indie-business'
      : 'https://tradly.app/pricing/marketplace';
  const openPlans = () => window.open(pricingUrl, '_blank', 'noopener');

  return (
    <div
      className="feature-upgrade-overlay"
      role="presentation"
      onMouseDown={blocking ? undefined : onClose}>
      <section
        className="feature-upgrade-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feature-upgrade-title"
        onMouseDown={event => event.stopPropagation()}
        style={{
          background: panelBackground,
          borderLeft: `1px solid ${border}`,
          '--feature-upgrade-text': workflowText,
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
          }}>
          <div
            aria-hidden="true"
            style={{
              display: 'grid',
              width: 44,
              height: 44,
              placeItems: 'center',
              borderRadius: 12,
              background: mutedSurface,
              color: colors.AppNewGreen,
              fontSize: 22,
            }}>
            ✦
          </div>
          {!blocking && (
          <button
            type="button"
            aria-label={t('upgrade_page.panel.close', 'Close upgrade panel')}
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              border: `1px solid ${border}`,
              borderRadius: 8,
              background: 'transparent',
              color: colors.primaryText,
              cursor: 'pointer',
              fontSize: 22,
              lineHeight: 1,
            }}>
            ×
          </button>
          )}
        </div>

        <p
          style={{
            margin: '24px 0 8px',
            color: colors.AppNewGreen,
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}>
          {content.eyebrow}
        </p>
        <h2
          id="feature-upgrade-title"
          style={{margin: 0, color: colors.primaryText, fontSize: 28, lineHeight: 1.2}}>
          {content.title}
        </h2>
        <p
          style={{
            margin: '14px 0 0',
            color: colors.secondaryText,
            fontSize: 16,
            lineHeight: 1.55,
          }}>
          {contextLabel && !blocking ? `${contextLabel}: ${content.description}` : content.description}
          {blocking && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => setShowMore(value => !value)}
                style={{
                  padding: 0,
                  border: 0,
                  background: 'none',
                  color: colors.AppNewGreen,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: 13,
                  textDecoration: 'underline',
                }}>
                {showMore ? t('upgrade_page.panel.show_less', 'Show less') : t('upgrade_page.panel.see_more', 'See more')}
              </button>
            </>
          )}
        </p>
        {blocking && showMore && (
          <p style={{margin: '12px 0 0', color: colors.secondaryText, fontSize: 14, lineHeight: 1.55}}>
            {content.more}
          </p>
        )}

        <div className="feature-upgrade-plans">
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12}}>
            <p style={{margin: 0, color: colors.primaryText, fontSize: 15, fontWeight: 800}}>
              {blocking ? t('upgrade_page.panel.choose_to_continue', 'Choose a plan to continue') : t('upgrade_page.panel.more_ways_to_grow', 'More ways to grow')}
            </p>
            {!isAlaCarte && hasYearly && (
              <div role="group" style={{display: 'flex', flexShrink: 0, padding: 2, border: `1px solid ${border}`, borderRadius: 999}}>
                {[false, true].map(isAnnual => (
                  <button
                    key={String(isAnnual)}
                    type="button"
                    aria-pressed={annual === isAnnual}
                    onClick={() => setAnnual(isAnnual)}
                    style={{
                      border: 0,
                      borderRadius: 999,
                      padding: '3px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: annual === isAnnual ? mutedSurface : 'transparent',
                      color: annual === isAnnual ? colors.primaryText : colors.secondaryText,
                    }}>
                    {isAnnual ? t('upgrade_page.panel.yearly', 'Yearly') : t('upgrade_page.panel.monthly', 'Monthly')}
                    {isAnnual && yearlyDiscountPercent > 0 && (
                      <span style={{marginLeft: 4, color: colors.AppNewGreen}}>-{yearlyDiscountPercent}%</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p style={{margin: '5px 0 0', color: colors.secondaryText, fontSize: 13, lineHeight: 1.45}}>
            {blocking ? content.intro : t('upgrade_page.panel.plan_includes_feature', 'Each plan includes this feature plus additional capacity for your next stage.')}
          </p>
          {isAlaCarte && (
            <article
              className="feature-upgrade-plan feature-upgrade-plan--recommended"
              style={{borderColor: colors.AppNewGreen, background: mutedSurface}}>
              <h3 style={{margin: 0, color: colors.primaryText, fontSize: 18}}>{t('upgrade_page.panel.add_credits', 'Add credits or products')}</h3>
              <p style={{margin: '12px 0', color: colors.secondaryText, fontSize: 13, lineHeight: 1.45}}>
                {t('upgrade_page.panel.ala_carte_description', 'Your workspace is billed à la carte. Top up credits or add the products you need.')}
              </p>
              <button
                type="button"
                onClick={() => window.location.assign('/workspace/plans?package=ala-carte')}
                className="feature-upgrade-plan__cta feature-upgrade-plan__cta--primary"
                style={{background: colors.AppNewGreen}}>
                {t('upgrade_page.panel.open_ala_carte', 'Open à la carte')}
              </button>
            </article>
          )}
          {(isAlaCarte ? [] : planCards).map((plan, index) => {
            const isRecommended = index === 0;
            return (
              <article
                className={`feature-upgrade-plan ${isRecommended ? 'feature-upgrade-plan--recommended' : ''}`}
                key={plan.name}
                style={{borderColor: isRecommended ? colors.AppNewGreen : border, background: isRecommended ? mutedSurface : panelBackground}}>
                <div className="feature-upgrade-plan__heading">
                  <div>
                    <h3 style={{margin: 0, color: colors.primaryText, fontSize: 18}}>{plan.title || plan.name}</h3>
                    {plan.label && <p style={{margin: '3px 0 0', color: colors.AppNewGreen, fontSize: 12, fontWeight: 750}}>{plan.label}</p>}
                  </div>
                  {isRecommended && <span className="feature-upgrade-plan__badge">{t('upgrade_page.panel.recommended', 'Recommended')}</span>}
                </div>
                {priceFor(plan) && (
                  <p style={{margin: '10px 0 0', color: colors.primaryText, fontSize: 22, fontWeight: 800}}>
                    {/* Yearly: the monthly equivalent first, then the yearly charge and the saving (same as the pricing page) */}
                    {formatCurrency(annual ? Number(priceFor(plan).amount) / 12 : priceFor(plan).amount, priceFor(plan).currency)}
                    <span style={{color: colors.secondaryText, fontSize: 13, fontWeight: 500}}> {t('upgrade_page.panel.per_month', '/ month')}</span>
                  </p>
                )}
                {annual && priceFor(plan) && (
                  <p style={{margin: '4px 0 0', color: colors.secondaryText, fontSize: 13, lineHeight: 1.45}}>
                    {t('workspace.plans.per_year_amount', {amount: formatCurrency(priceFor(plan).amount, priceFor(plan).currency)}, 'per year ({amount})')}{' '}
                    {t('workspace.plans.billed_yearly', 'billed yearly')}
                    {yearlySavingFor(plan) && (
                      <span style={{display: 'block', color: colors.AppNewGreen, fontWeight: 700}}>
                        {t('workspace.plans.yearly_savings', {amount: formatCurrency(yearlySavingFor(plan).amount, priceFor(plan).currency)}, 'Save {amount} yearly')}
                      </span>
                    )}
                  </p>
                )}
                <p style={{margin: '12px 0', color: colors.secondaryText, fontSize: 13, lineHeight: 1.45}}>{plan.description}</p>
                <ul>
                  {[...planKitLines(t, priceFor(plan)?.key || plan.name), ...planLimitLines(priceFor(plan)?.limits, t)].map(line => <li key={line}>{line}</li>)}
                  {planUpgradeHint(t, priceFor(plan)?.key || plan.name) && <li style={{opacity: 0.75}}>{planUpgradeHint(t, priceFor(plan)?.key || plan.name)}</li>}
                  {plan.benefits.map(benefit => <li key={benefit}>{benefit}</li>)}
                </ul>
                <button
                  type="button"
                  onClick={() => choosePlan(plan.name)}
                  disabled={Boolean(busyPlan)}
                  className={isRecommended ? 'feature-upgrade-plan__cta feature-upgrade-plan__cta--primary' : 'feature-upgrade-plan__cta'}
                  style={isRecommended ? {background: colors.AppNewGreen} : {borderColor: border, color: colors.primaryText}}>
                  {busyPlan === plan.name
                    ? t('upgrade_page.panel.please_wait', 'Please wait…')
                    : isRecommended
                      ? t('upgrade_page.panel.unlock_plan', {plan: plan.title || plan.name}, 'Unlock {plan}')
                      : t('upgrade_page.panel.choose_plan', {plan: plan.title || plan.name}, 'Choose {plan}')}
                </button>
              </article>
            );
          })}
          {planError && (
            <p role="alert" style={{margin: '10px 0 0', color: colors.AppRed, fontSize: 13}}>{planError}</p>
          )}
          <button type="button" className="feature-upgrade-compare-link" onClick={() => openPlans()}>
            {t('upgrade_page.panel.compare_plans', 'Compare all plan details')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default FeatureUpgradePanel;
